import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AGENT_LIST, type AgentSlug } from '../../agents/registry.ts';
import { agentsEnabledFor, isAgentEnabledFor } from '../../agents/agentAccess.ts';
import { getLastAgent, hasSeenIntro } from '../../agents/preferences.ts';
import { useAuth } from '../../auth/useAuth.ts';
import { LISTED_BLOG_POSTS } from '../../config/blogPosts.ts';
import { PICO_V2 } from '../../config/models.ts';
import { WIDGETS } from '../../widgets/catalog.ts';
import './AppShell.css';

/** How long the chooser lingers before auto-continuing to the sole available
 *  agent. Long enough to take in the chooser and cancel comfortably; short
 *  enough not to feel stuck. */
const AUTO_CONTINUE_MS = 5000;
const AUTO_CONTINUE_SECONDS = AUTO_CONTINUE_MS / 1000;

/** A first-time selection routes through the agent's intro tour; afterwards it
 *  goes straight to the console. */
function destinationFor(slug: AgentSlug): string {
  return hasSeenIntro(slug) ? `/agent/${slug}` : `/agent-intro/${slug}`;
}

/**
 * Signed-in landing surface: a chooser of the available agents. Each available
 * agent is selectable; agents not yet online are shown but disabled. When
 * exactly one agent is available the chooser auto-continues to it after a short
 * countdown the user can cancel.
 */
export default function AppShell() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [autoCancelled, setAutoCancelled] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_CONTINUE_SECONDS);

  // The single enterable agent for THIS user (if there's exactly one) drives
  // the auto-continue — per-user, since group-gated agents (Vera) widen the
  // list only for eligible accounts.
  const enabledAgents = agentsEnabledFor(user);
  const soleAgent = enabledAgents.length === 1 ? enabledAgents[0] : null;
  // Only auto-continue for first-time users (no agent entered yet). Once a
  // user has a last-used agent, the chooser is a deliberate destination — they
  // navigated here (e.g. via the console's brand link) to pick — so it stays
  // put instead of bouncing them back.
  const isFirstVisit = useRef(getLastAgent() === null).current;
  const autoContinuing = soleAgent !== null && isFirstVisit && !autoCancelled;

  useEffect(() => {
    // autoContinuing embeds soleAgent !== null (aliased-condition
    // narrowing carries it past this guard).
    if (!autoContinuing) {
      return;
    }
    setSecondsLeft(AUTO_CONTINUE_SECONDS);
    const ticker = window.setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    const timer = window.setTimeout(() => {
      navigate(destinationFor(soleAgent.slug), { replace: true });
    }, AUTO_CONTINUE_MS);
    return () => {
      window.clearInterval(ticker);
      window.clearTimeout(timer);
    };
  }, [autoContinuing, soleAgent, navigate]);

  async function handleSignOut() {
    await signOut();
    navigate('/auth/signed-out', { replace: true });
  }

  function selectAgent(slug: AgentSlug) {
    navigate(destinationFor(slug));
  }

  const greeting =
    user?.preferredUsername ??
    user?.givenName ??
    user?.email ??
    'agent operator';

  return (
    <div className="om-app">
      <header className="om-app__header">
        <div>
          <h1 className="om-app__title">
            OculusMind.AI <span>console</span>
          </h1>
          <p className="om-app__copy">
            Welcome, {greeting}. Talk to an agent, or wire them into your own systems.
          </p>
        </div>
        <nav className="om-app__nav">
          <Link to="/app/account" className="om-app__link">
            Account
          </Link>
          <button
            type="button"
            className="om-app__signout"
            onClick={() => {
              void handleSignOut();
            }}
          >
            Sign out
          </button>
        </nav>
      </header>

      {autoContinuing ? (
        <div className="om-autocontinue" role="status">
          <span className="om-autocontinue__dot" />
          <span>
            Taking you to <strong>{soleAgent.name}</strong> in {secondsLeft}s…
          </span>
          <button
            type="button"
            className="om-autocontinue__stay"
            onClick={() => setAutoCancelled(true)}
          >
            Stay here
          </button>
          <span
            className="om-autocontinue__bar"
            style={{ animationDuration: `${AUTO_CONTINUE_MS}ms` }}
            aria-hidden="true"
          />
        </div>
      ) : null}

      {/* ---- Human Interface: real-time conversation with an agent ---- */}
      <section className="om-iface">
        <div className="om-iface__head">
          <span className="om-iface__eyebrow">Human Interface</span>
          <p className="om-iface__lede">
            Converse with an agent in real time — it searches, reasons, and acts for you.
          </p>
        </div>
        <ul className="om-chooser" aria-label="Agents">
          {AGENT_LIST.map((agent) => (
            <li key={agent.slug}>
              {isAgentEnabledFor(agent.slug, user) ? (
                <button
                  type="button"
                  className="om-chooser__card om-chooser__card--available"
                  onClick={() => selectAgent(agent.slug)}
                >
                  <span className="om-chooser__portrait">
                    <img
                      src={agent.image}
                      alt=""
                      loading="eager"
                      decoding="async"
                      draggable={false}
                    />
                  </span>
                  <span className="om-chooser__info">
                    <span className="om-chooser__name">
                      {agent.name}
                      {agent.beta === true ? (
                        <span className="om-chooser__beta">Beta</span>
                      ) : null}
                    </span>
                    <span className="om-chooser__role">{agent.role}</span>
                    <span className="om-chooser__status om-chooser__status--active">
                      <span className="om-chooser__status-dot" /> Active · online
                    </span>
                    <span className="om-chooser__enter">Activate →</span>
                  </span>
                </button>
              ) : (
                <div
                  className="om-chooser__card om-chooser__card--soon"
                  aria-disabled="true"
                >
                  <span className="om-chooser__portrait">
                    <img
                      src={agent.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                  </span>
                  <span className="om-chooser__info">
                    {/* No Beta badge on an "in training" card — the badge marks
                        an available-but-maturing agent (Maximus, for eligible
                        admin/test users), and "Beta" + "In training" together
                        reads as contradictory. */}
                    <span className="om-chooser__name">{agent.name}</span>
                    <span className="om-chooser__role">{agent.role}</span>
                    <span className="om-chooser__status om-chooser__status--training">
                      <span className="om-chooser__status-dot" /> In training
                    </span>
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* ---- Research Briefs: what the agents publish on a schedule ---- */}
      <section className="om-iface">
        <div className="om-iface__head">
          <span className="om-iface__eyebrow">Research Briefs</span>
          <p className="om-iface__lede">
            Your agents research and publish on a schedule — no conversation needed.
          </p>
        </div>
        {/* Plain <a>, NOT a router <Link>: /widgets is static HTML published from
            the widgets bucket, not an SPA route. A Link intercepts the click,
            matches nothing, and falls through to App.tsx's path="*" catch-all,
            which redirects to "/" — the reader lands back on the homepage with no
            error anywhere. Same reason as SiteHeader and GuestDock; AppShell.test
            pins it by clicking and asserting the router never moved. This card is
            the ONLY briefs entry point signed-in users get, because /app renders
            its own header rather than SiteHeader. */}
        <a href="/widgets" className="om-iface-card">
          <span className="om-iface-card__icon" aria-hidden="true">
            <svg
              viewBox="0 0 48 48"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 7 H36 A2 2 0 0 1 38 9 V39 A2 2 0 0 1 36 41 H12 A2 2 0 0 1 10 39 V9 A2 2 0 0 1 12 7 Z" />
              <path d="M17 17 H31" />
              <path d="M17 24 H31" />
              <path d="M17 31 H25" />
            </svg>
          </span>
          <span className="om-iface-card__info">
            <span className="om-iface-card__name">Latest briefs</span>
            <span className="om-iface-card__desc">
              Standing research your agents run through the week — {WIDGETS.length} briefs,
              published with their sources.
            </span>
            <ul className="om-iface-card__tags" aria-label="Brief topics">
              {WIDGETS.map((widget) => (
                <li key={widget.id}>{widget.kicker}</li>
              ))}
            </ul>
            <span className="om-iface-card__enter">Read today&rsquo;s briefs →</span>
          </span>
        </a>
      </section>

      {/* ---- Blog: long-form research the agents write ---- */}
      <section className="om-iface">
        <div className="om-iface__head">
          <span className="om-iface__eyebrow">Blog</span>
          <p className="om-iface__lede">
            Long-form research your agents write, with every source checked.
          </p>
        </div>
        {/* A router <Link> is right HERE, unlike the briefs card above: /blog
            itself is an SPA route (App.tsx). The posts it lists are static
            files, and Blog.tsx links those with plain anchors. */}
        <Link to="/blog" className="om-iface-card">
          <span className="om-iface-card__icon" aria-hidden="true">
            <svg
              viewBox="0 0 48 48"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 11 C14 9 20 9 24 13 C28 9 34 9 40 11 V37 C34 35 28 35 24 39 C20 35 14 35 8 37 Z" />
              <path d="M24 13 V39" />
            </svg>
          </span>
          <span className="om-iface-card__info">
            <span className="om-iface-card__name">Blog</span>
            <span className="om-iface-card__desc">
              {LISTED_BLOG_POSTS.length} {LISTED_BLOG_POSTS.length === 1 ? 'post' : 'posts'}{' '}
              researched and written by our agents, every source verified.
            </span>
            <ul className="om-iface-card__tags" aria-label="Blog topics">
              {[...new Set(LISTED_BLOG_POSTS.map((post) => post.kicker))].map((kicker) => (
                <li key={kicker}>{kicker}</li>
              ))}
            </ul>
            <span className="om-iface-card__enter">Read the blog →</span>
          </span>
        </Link>
      </section>

      {/* ---- Machine Interface: programmatic access via the Integrations API ---- */}
      <section className="om-iface">
        <div className="om-iface__head">
          <span className="om-iface__eyebrow">Machine Interface</span>
          <p className="om-iface__lede">Wire your agents into your own systems and code.</p>
        </div>
        <Link to="/integrations" className="om-iface-card">
          <span className="om-iface-card__icon" aria-hidden="true">
            <svg
              viewBox="0 0 48 48"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 15 L7 24 L17 33" />
              <path d="M31 15 L41 24 L31 33" />
              <path d="M27 12 L21 36" />
            </svg>
          </span>
          <span className="om-iface-card__info">
            <span className="om-iface-card__name">Integrations &amp; API</span>
            <span className="om-iface-card__desc">
              Call your agents straight from your own code.
            </span>
            <ul className="om-iface-card__tags" aria-label="Interface types">
              <li>Agent-to-Agent</li>
              <li>POST</li>
              <li>cURL</li>
              <li>TypeScript</li>
            </ul>
            <span className="om-iface-card__enter">Open integrations →</span>
          </span>
        </Link>
      </section>

      {/* ---- Models: the open-weight models we train and publish ---- */}
      <section className="om-iface">
        <div className="om-iface__head">
          <span className="om-iface__eyebrow">Models</span>
          <p className="om-iface__lede">The open-weight models we train for agent work.</p>
        </div>
        {/* A router <Link>: /models is an SPA route (App.tsx), like /blog. */}
        <Link to="/models" className="om-iface-card">
          <span className="om-iface-card__icon" aria-hidden="true">
            <svg
              viewBox="0 0 48 48"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M24 6 L40 15 V33 L24 42 L8 33 V15 Z" />
              <path d="M8 15 L24 24 L40 15" />
              <path d="M24 24 V42" />
            </svg>
          </span>
          <span className="om-iface-card__info">
            <span className="om-iface-card__name">Open models</span>
            <span className="om-iface-card__desc">
              {PICO_V2.name}, our 8B tool-calling model — benchmarks and downloads, Apache-2.0.
            </span>
            <ul className="om-iface-card__tags" aria-label="Model facts">
              <li>{PICO_V2.name}</li>
              <li>8B</li>
              <li>Tool calling</li>
              <li>Apache-2.0</li>
            </ul>
            <span className="om-iface-card__enter">See the models →</span>
          </span>
        </Link>
      </section>
    </div>
  );
}
